import { AuthResponse, LoginCredentials, User } from '../types';

const API_URL = '/api';

class ApiClient {
    private token: string | null = localStorage.getItem('auth_token');

    constructor() {
        if (this.token) {
            console.log('API Client initialized with token');
        }
    }

    private getHeaders(): HeadersInit {
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };
        if (this.token) {
            headers['Authorization'] = `Bearer ${this.token}`;
        }
        return headers;
    }

    setToken(token: string) {
        this.token = token;
        localStorage.setItem('auth_token', token);
    }

    clearToken() {
        this.token = null;
        localStorage.removeItem('auth_token');
    }

    async get<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
        // Ensure endpoint doesn't have a leading slash if joining manually, 
        // or let URL handle it correctly by ensuring base ends with a slash.
        const baseUrl = window.location.origin + API_URL + '/';
        const url = new URL(endpoint.startsWith('/') ? endpoint.slice(1) : endpoint, baseUrl);

        if (params) {
            Object.keys(params).forEach(key =>
                url.searchParams.append(key, String(params[key]))
            );
        }

        console.log(`[API DEBUG] GET Request: ${url.toString()}`);

        const response = await fetch(url.toString(), {
            method: 'GET',
            headers: this.getHeaders(),
        });

        if (!response.ok) {
            console.error(`[API DEBUG] GET Failed: ${response.status} ${response.statusText}`);
            throw new Error(`API Error: ${response.statusText}`);
        }

        const data = await response.json();
        console.log(`[API DEBUG] GET Success:`, data);
        return data;
    }

    async post<T>(endpoint: string, data: any): Promise<T> {
        const response = await fetch(`${API_URL}${endpoint}`, {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(error.error || `API Error: ${response.statusText}`);
        }

        return response.json();
    }

    async patch<T>(endpoint: string, data: any): Promise<T> {
        const response = await fetch(`${API_URL}${endpoint}`, {
            method: 'PATCH',
            headers: this.getHeaders(),
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(error.error || `API Error: ${response.statusText}`);
        }

        return response.json();
    }

    async delete<T>(endpoint: string): Promise<T> {
        const response = await fetch(`${API_URL}${endpoint}`, {
            method: 'DELETE',
            headers: this.getHeaders(),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(error.error || `API Error: ${response.statusText}`);
        }

        return response.json();
    }
}

export const api = new ApiClient();
